import api from "./api";
import { postEventStream } from "./eventStream";

// User stories for a process, generated by the backend: an epic, one story per
// task, decision and sub-process, and a list of problems found in the model.
// The server allows 5 requests a minute, so only call this on a button click.

// With AI on, generation takes 30 s to 4.5 min.
const AI_TIMEOUT_MS = 300_000;
const RULES_TIMEOUT_MS = 60_000;

const MESSAGES = {
  403: "You do not have access to this process.",
  404: "Process not found.",
  429: "Too many requests. Wait a minute and try again.",
};

export function userStoriesErrorMessage(error) {
  if (error?.timeout) {
    return "Generating the user stories took too long. Please try again.";
  }
  // 400 carries the reason (bad ID, no tasks yet) in the response message.
  if (error?.status === 400 && error.message) return error.message;
  return MESSAGES[error?.status] || "Something went wrong. Please try again.";
}

// POST /process/:id/user-stories/stream?ai=true|false
// Live version of generateUserStories. Events, in order: `rules` (the full
// result in template text), then while the AI works `status`, `reasoning` and
// one `task` per TASK story, and finally `done` (the validated result) or
// `error`. `handlers` maps each event name to a callback taking its data.
export function streamUserStories(processId, { ai = true, signal } = {}, handlers) {
  if (!processId) throw new Error("Process ID is required");
  return postEventStream(
    `/process/${encodeURIComponent(processId)}/user-stories/stream?ai=${
      ai ? "true" : "false"
    }`,
    (event, data) => handlers[event]?.(data),
    { signal },
  );
}

// POST /process/:id/user-stories?ai=true|false
export async function generateUserStories(processId, { ai = true } = {}) {
  if (!processId) throw new Error("Process ID is required");
  const endpoint = `/process/${encodeURIComponent(processId)}/user-stories?ai=${
    ai ? "true" : "false"
  }`;
  try {
    // The page shows errors inline, so keep the global error page out of it;
    // an expired session (401) still goes to the app-wide handler (login).
    return await api.request(endpoint, {
      method: "POST",
      timeout: ai ? AI_TIMEOUT_MS : RULES_TIMEOUT_MS,
      suppressErrorPage: true,
    });
  } catch (error) {
    if (error?.status === 401) {
      window.dispatchEvent(
        new CustomEvent("app:http-error", {
          detail: { status: 401, message: error.message, endpoint },
        }),
      );
    }
    throw error;
  }
}
